/*
Country Selector Custom Element

HTML Usage:
<country-selector data-options='{}'>
  <button data-country-selector-trigger>
    <span data-current-flag>🇳🇱</span>
    <span data-current-country>Netherlands</span>
    <svg data-country-selector-icon>...</svg>
  </button>
  <div data-country-selector-dropdown>
    <a href="https://iluvejouets.com">
      <span data-alternative-flag>🇫🇷</span>
      <span data-alternative-country>France</span>
    </a>
  </div>
</country-selector>
*/

if (!customElements.get('country-selector')) {
  class CountrySelector extends HTMLElement {
    constructor() {
      super();

      // Options
      this.options = {
        toggleClass: 'country-selector--is-open',
        closeOnOutsideClick: true,
        closeOnEscape: true,
      };

      // Get options from element data and combine with this.options
      if (this?.dataset?.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions,
        };
      }

      // Elements
      this.trigger = this.querySelector('[data-country-selector-trigger]');
      this.dropdown = this.querySelector('[data-country-selector-dropdown]');
      this.icon = this.querySelector('[data-country-selector-icon]');

      // State
      this.isOpen = false;

      // Bind event handlers to preserve references for cleanup
      this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundToggle = this.toggle.bind(this);

      // Initialize
      this.connectedCallback();
    }

    connectedCallback() {
      if (!this.trigger || !this.dropdown) {
        console.warn('Country selector: Missing required elements');
        return;
      }

      this.setupEventListeners();
      this.setupAccessibility();
    }

    setupEventListeners() {
      // Toggle dropdown
      this.trigger.addEventListener('click', this.boundToggle);

      // Close on outside click
      if (this.options.closeOnOutsideClick) {
        document.addEventListener('click', this.boundHandleOutsideClick);
      }

      // Close on escape key
      if (this.options.closeOnEscape) {
        document.addEventListener('keydown', this.boundHandleKeydown);
      }
    }

    setupAccessibility() {
      // Set initial ARIA attributes
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-haspopup', 'true');
      this.dropdown.setAttribute('role', 'menu');
    }

    toggle(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.isOpen = true;
      this.dropdown.classList.remove('hidden');
      this.icon.style.transform = 'rotate(180deg)';
      this.trigger.setAttribute('aria-expanded', 'true');
      this.trigger.classList.add(this.options.toggleClass);
    }

    close() {
      this.isOpen = false;
      this.dropdown.classList.add('hidden');
      this.icon.style.transform = 'rotate(0deg)';
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.classList.remove(this.options.toggleClass);
    }

    handleOutsideClick(event) {
      // Only close if the dropdown is open and the click is outside the component
      if (this.isOpen && !this.contains(event.target)) {
        this.close();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
        this.trigger.focus();
      }
    }

    disconnectedCallback() {
      // Clean up event listeners
      if (this.options.closeOnOutsideClick) {
        document.removeEventListener('click', this.boundHandleOutsideClick);
      }
      if (this.options.closeOnEscape) {
        document.removeEventListener('keydown', this.boundHandleKeydown);
      }
    }
  }

  customElements.define('country-selector', CountrySelector);
}
